import { useReadDocuments } from '@/api/accounting-dealer'
import { DocumentInput } from '@/components'
import { ErrorOutline } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'

type CompanyDocumentsProps = {
  companyKey: string
  documentRequestKey: string
}

export function CompanyDocuments({
  companyKey,
  documentRequestKey,
}: CompanyDocumentsProps) {
  const {
    data: companyDocuments,
    error: companyDocumentsError,
    isLoading: isCompanyDocumentsLoading,
  } = useReadDocuments({
    entity_key: companyKey,
    entity_type: 'company',
    document_request_key: documentRequestKey,
    status: 'approved',
  })

  if (isCompanyDocumentsLoading) {
    return (
      <Box textAlign="center">
        <Typography>Carregando documentos da PJ...</Typography>
      </Box>
    )
  }

  if (companyDocumentsError) {
    return (
      <Box textAlign="center">
        <ErrorOutline color="error" fontSize="large" />
        <Typography color="gray">
          Não foi possível carregar os documentos da PJ.
        </Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={3}>
      <DocumentInput
        fieldName="company.bank_statement_pdf"
        label="Extrato da conta corrente da PJ em PDF"
        entity={{
          key: companyKey,
          type: 'company',
        }}
        documents={companyDocuments || []}
        documentType="bank_statement_pdf"
        document_request_key={documentRequestKey}
        required
        accept={{
          'application/pdf': ['.pdf'],
        }}
      />

      <DocumentInput
        fieldName="company.bank_statement"
        label="Extrato da conta corrente PJ em planilha ou ofx, identificando os pagamentos"
        entity={{
          key: companyKey,
          type: 'company',
        }}
        documents={companyDocuments || []}
        documentType="bank_statement"
        document_request_key={documentRequestKey}
        accept={{
          'application/x-ofx': ['.ofx'],
          'application/vnd.ms-excel': ['.xls'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
            '.xlsx',
          ],
          'text/csv': ['.csv'],
          'text/plain': ['.txt'],
          'text/html': ['.html'],
          'text/xml': ['.xml'],
        }}
      />

      <DocumentInput
        fieldName="company.bank_investment_statement"
        label="Extrato de aplicações financeiras e/ou investimentos da PJ, se
                houver"
        entity={{
          key: companyKey,
          type: 'company',
        }}
        documents={companyDocuments || []}
        documentType="bank_investment_statement"
        document_request_key={documentRequestKey}
        accept={{
          'application/pdf': ['.pdf'],
        }}
      />

      <DocumentInput
        fieldName="company.other"
        label="Outros (Notas fiscais de Serviços Tomados, Notas fiscais de Compra e Venda; outros)"
        entity={{
          key: companyKey,
          type: 'company',
        }}
        documents={companyDocuments || []}
        documentType="other"
        document_request_key={documentRequestKey}
        accept={{
          'application/pdf': ['.pdf'],
        }}
      />
    </Stack>
  )
}
